'use client';

import { ReactNode } from 'react';

import { ArrivalOrderSlotsGrid } from './arrival-order-slots-grid.component';

import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { ScheduleSlotFilters, useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { useIsGreaterThan } from '@/hooks/use-is-greather-than.hook';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';

const MODAL_DAYS_NARROW = 2;
const MODAL_DAYS_WIDE = 3;

interface ArrivalOrderModalProps {
  scheduleIds: string[];
  entityName: string;
  entityAvatar?: string | null;
  entityType: EntityTypeEnum;
  partnerId: string;
  price: number;
  busySlotIds?: ReadonlySet<string>;
  open: boolean;
  slotFilters: ScheduleSlotFilters;
  dataTestId?: string;
  closeDataTestId?: string;
  isLoadingSchedule?: boolean;
  errorContent?: ReactNode;
  onOpenChange(open: boolean): void;
  onClose?(): void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

export function ArrivalOrderModal({
  scheduleIds,
  entityName,
  entityAvatar,
  entityType,
  partnerId,
  price,
  busySlotIds,
  open,
  slotFilters,
  dataTestId,
  closeDataTestId,
  isLoadingSchedule = false,
  errorContent,
  onOpenChange,
  onClose,
  onSelectSlot,
}: ArrivalOrderModalProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="center-v2"
        className="md:h-fit md:w-[90vw] md:rounded-2xl md:max-h-[67vh] md:max-w-[640px] p-0"
        hasHeader
        data-testid={dataTestId}
      >
        <ArrivalOrderModalContent
          scheduleIds={scheduleIds}
          entityName={entityName}
          entityAvatar={entityAvatar}
          entityType={entityType}
          partnerId={partnerId}
          price={price}
          busySlotIds={busySlotIds}
          slotFilters={slotFilters}
          closeDataTestId={closeDataTestId}
          isLoadingSchedule={isLoadingSchedule}
          errorContent={errorContent}
          onClose={onClose}
          onSelectSlot={onSelectSlot}
        />
      </SheetContent>
    </Sheet>
  );
}

export type ArrivalOrderModalContentProps = Omit<ArrivalOrderModalProps, 'open' | 'dataTestId' | 'onOpenChange'>;

export function ArrivalOrderModalContent({
  scheduleIds,
  entityName,
  entityAvatar,
  entityType,
  partnerId,
  price,
  busySlotIds,
  slotFilters,
  closeDataTestId,
  isLoadingSchedule = false,
  errorContent,
  onClose,
  onSelectSlot,
}: ArrivalOrderModalContentProps) {
  const isWide = useIsGreaterThan('sm');
  const limitDays = isWide ? MODAL_DAYS_WIDE : MODAL_DAYS_NARROW;

  const { isLoading, days, hasMore, offset, goNext, goPrev } = useScheduleSlots({
    scheduleIds,
    limitDays,
    filters: slotFilters,
  });

  return (
    <>
      <div className="flex flex-col w-full pt-5">
        <div className="flex items-start gap-2 pl-6 pr-5 pb-4 w-full">
          <Typography variant={TypographyVariant.MODAL_TITLE} className="flex-1">
            Horários disponíveis
          </Typography>
          <SheetClose
            asChild
            className="self-start"
            data-testid={closeDataTestId}
            onClick={onClose}
          >
            <ModalCloseButton />
          </SheetClose>
        </div>
      </div>

      <div className="flex flex-col gap-5 px-6 pt-4 pb-5 w-full overflow-y-auto">
        <div className="flex items-start gap-4 w-full">
          <EntityAvatar
            alt={`Foto de perfil de ${entityName}`}
            size="lg"
            src={entityAvatar ?? undefined}
            type={entityType}
            className="rounded-lg"
          />
          <Typography
            variant={TypographyVariant.MEDIUM_SUBTITLE}
            className="flex-1 text-green-500 font-semibold line-clamp-2 leading-6 sm:leading-7"
            as="span"
          >
            {entityName}
          </Typography>
        </div>

        {errorContent}

        {!errorContent && (isLoadingSchedule || isLoading
          ? <SlotsSkeleton variant="modal" columns={limitDays} />
          : (
              <ArrivalOrderSlotsGrid
                days={days}
                hasMore={hasMore}
                offset={offset}
                partnerId={partnerId}
                price={price}
                busySlotIds={busySlotIds}
                slotClassName="py-2 text-base"
                onPrev={goPrev}
                onNext={goNext}
                onSelectSlot={onSelectSlot}
              />
            ))}
      </div>
    </>
  );
}
